import Button from '@mui/material/Button';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { updateAccountTypeOrder } from '@/api/user';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { Select } from '@/components/Form/Select';
import { ActionBar } from '@/components/Layout/Layout.styled';
import { QuestionProps } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionDrawer.interface';
import { QuestionForm } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionForm';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectUserAccountTypes } from '@/redux/user-data/selectors';

import { getDataTestId } from '@/utils/browser';

export function QuestionAccountTypeOrder({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const userAccountTypes = useAppSelector(selectUserAccountTypes);
  const hasOnlyOneAccountType = userAccountTypes.length === 1;

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors: visibleErrors, isValid },
    watch,
  } = useForm<{
    primaryAccountId: string;
    secondaryAccountId: string;
  }>({
    defaultValues: {
      primaryAccountId: hasOnlyOneAccountType ? userAccountTypes[0].id : '',
      secondaryAccountId: '',
    },
    mode: 'all',
  });

  const primaryAccountId = watch('primaryAccountId');

  useEffect(() => {
    setValue('secondaryAccountId', '');
  }, [primaryAccountId, setValue]);

  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: updateAccountTypeOrder,
    onFinished: onAnswered,
  });

  const onSubmit = handleSubmit(async payload => {
    mutate(payload);
  });

  const primaryAccountOption = userAccountTypes.map(type => ({
    text: `${type.feeCurrency} ${type.name}`,
    value: type.id,
  }));

  const secondaryAccountOption = userAccountTypes.map(type => ({
    text: `${type.feeCurrency} ${type.name}`,
    value: type.id,
    disabled: type.id === primaryAccountId,
  }));

  return (
    <QuestionForm
      {...props}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      <Select
        options={primaryAccountOption}
        control={control}
        name="primaryAccountId"
        error={!!visibleErrors.primaryAccountId}
        helperText={visibleErrors.primaryAccountId?.message}
        rules={{
          required: t(
            'questionDrawer.AccountTypeOrder.primaryAccount.error.required',
          ),
        }}
        fullWidth
        placeholder={t(
          'questionDrawer.AccountTypeOrder.primaryAccount.placeholder',
        )}
        useDrawerOnMobile
        label={t('questionDrawer.AccountTypeOrder.primaryAccount.label')}
        disabled={hasOnlyOneAccountType || isPending}
        inputProps={{
          ...getDataTestId('question-drawer-primary-account-select'),
        }}
      />
      {!hasOnlyOneAccountType && (
        <Select
          options={secondaryAccountOption}
          control={control}
          name="secondaryAccountId"
          error={!!visibleErrors.secondaryAccountId}
          helperText={visibleErrors.secondaryAccountId?.message}
          rules={{
            required: t(
              'questionDrawer.AccountTypeOrder.secondaryAccount.error.required',
            ),
          }}
          fullWidth
          placeholder={t(
            'questionDrawer.AccountTypeOrder.secondaryAccount.placeholder',
          )}
          useDrawerOnMobile
          label={t('questionDrawer.AccountTypeOrder.secondaryAccount.label')}
          disabled={isPending}
          inputProps={{
            ...getDataTestId('question-drawer-secondary-account-select'),
          }}
        />
      )}
      <ActionBar>
        <Button
          {...getDataTestId(`question-drawer-primary-account-confirm-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {t('questionDrawer.AccountTypeOrder.button.confirm')}
        </Button>
      </ActionBar>
      <FormErrorLogger formName="QuestionAccountTypeOrder" control={control} />
    </QuestionForm>
  );
}
